import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimation } from 'framer-motion';
import { X, Heart, Shuffle, RotateCcw, Star, BadgeCheck, MessageCircle, ArrowLeft, CheckCircle, Award } from 'lucide-react';
import { Vendor } from '../../../types/vendor';
import { FavoritesAPI } from '../../../services/api/favoritesApi.ts';
import QuoteRequestModal from './QuoteRequestModal.tsx';
import { useNavigate } from 'react-router-dom';

interface VendorSwipeViewProps {
  vendors: Vendor[];
  onVendorSelect: (vendor: Vendor) => void;
  onMessageVendor: (vendor: Vendor) => void;
  onViewDetails: (vendor: Vendor) => void;
}

interface MessageOverlayProps {
  vendor: Vendor;
  onClose: () => void;
  onSendMessage: () => void;
  onViewDetails: (vendor: Vendor) => void;
}

const MessageOverlay: React.FC<MessageOverlayProps> = ({ vendor, onClose, onSendMessage, onViewDetails }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* Blurred background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF69B4]/30 to-[#FFDAB9]/30 backdrop-blur-xl" />
      
      {/* Content */}
      <div className="relative w-full max-w-[360px] mx-auto px-4">
        {/* Back button */}
        <button
          onClick={onClose}
          className="absolute -top-10 left-0 p-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-colors z-10"
        >
          <ArrowLeft size={16} />
        </button>

        {/* Vendor preview - more compact for small screens */}
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className="w-full bg-white/10 backdrop-blur-lg rounded-2xl overflow-hidden border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.1)] relative before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/10 before:to-white/5 before:pointer-events-none"
        >
          <div className="flex items-start">
            {/* Vendor image with gradient overlay */}
            <div className="relative h-40 w-32 flex-shrink-0 m-4 rounded-2xl overflow-hidden border-2 border-white/20 shadow-lg">
              <img
                src={vendor.image || '/themesOption/placeholder.jpg'}
                alt={vendor.name}
                className="w-full h-full object-cover rounded-2xl transform hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent rounded-2xl"></div>
            </div>

            {/* Vendor info */}
            <div className="p-4 flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">
                {vendor.name}
                {vendor.verified && (
                  <BadgeCheck className="inline-block w-5 h-5 text-[#2DCEA8] ml-2" />
                )}
              </h3>
              <p className="text-white/90 text-lg mb-4">{vendor.location}</p>
              
              {/* Type badges - horizontal layout */}
              <div className="flex gap-2 flex-wrap">
                <span className="inline-flex items-center px-4 py-1.5 text-sm font-medium rounded-full bg-white/20 backdrop-blur-sm text-white">
                  {vendor.vendorType}
                </span>
                {vendor.specialty && (
                  <span className="inline-flex items-center px-4 py-1.5 text-sm font-medium rounded-full bg-[#5D5FEF]/20 backdrop-blur-sm text-white">
                    {vendor.specialty}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3 p-4">
            <button
              onClick={() => onSendMessage()}
              className="py-3 px-4 rounded-2xl bg-[#FF4E8E] text-white text-base font-medium shadow-lg hover:shadow-xl hover:opacity-90 transition-all flex items-center justify-center gap-2"
            >
              <MessageCircle size={20} className="flex-shrink-0" />
              <span>Message</span>
            </button>
            <button
              onClick={() => onViewDetails(vendor)}
              className="py-3 px-4 rounded-2xl bg-[#5D5FEF] text-white text-base font-medium shadow-lg hover:shadow-xl hover:opacity-90 transition-all flex items-center justify-center gap-2"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span>Details</span>
            </button>
            <button
              onClick={onClose}
              className="col-span-2 py-3 rounded-2xl bg-white/10 backdrop-blur-sm text-white text-base font-medium hover:bg-white/20 transition-colors"
            >
              Not Now
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const VendorSwipeView: React.FC<VendorSwipeViewProps> = ({ vendors, onVendorSelect, onMessageVendor, onViewDetails }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right' | 'up' | null>(null);
  const [showMessageOverlay, setShowMessageOverlay] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Reset state when vendors change
  useEffect(() => {
    let mounted = true;

    const handleVendorChange = async () => {
      setIsTransitioning(true);
      
      // Reset all state immediately
      setCurrentIndex(0);
      setDirection(null);
      setShowMessageOverlay(false);
      setShowQuoteModal(false);
      setSelectedVendor(null);
      
      // Small delay to ensure clean state
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (mounted) {
        setIsTransitioning(false);
      }
    };

    handleVendorChange();
    
    return () => {
      mounted = false;
    };
  }, [vendors]);

  // Motion values for swipe animation
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-5, 5]);
  const scaleOnDrag = useTransform(y, [-150, 0], [0.97, 1]);
  const animateCard = useAnimation();

  // Transform values for swipe indicators
  const leftIndicatorOpacity = useTransform(x, [-50, -10], [1, 0]);
  const rightIndicatorOpacity = useTransform(x, [10, 50], [0, 1]);
  const upIndicatorOpacity = useTransform(y, [-50, -10], [1, 0]);

  // Get current vendors for display
  const currentVendors = useMemo(() => {
    if (!vendors || vendors.length === 0) return [];
    return vendors.slice(currentIndex, Math.min(currentIndex + 3, vendors.length));
  }, [vendors, currentIndex]);

  // Function to handle advancing to the next card
  const advanceCard = useCallback(() => {
    if (!vendors || vendors.length === 0) return;
    
    if (currentIndex < vendors.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      // Reset to beginning if we've reached the end
      setCurrentIndex(0);
    }
  }, [currentIndex, vendors]);

  // Reset position when index changes
  useEffect(() => {
    if (isTransitioning) return;

    x.set(0);
    y.set(0);
    setDirection(null);
    
    animateCard.start({ 
      x: 0, 
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: { type: "spring", stiffness: 300, damping: 20 }
    });
  }, [currentIndex, animateCard, x, y, isTransitioning]);

  const handleDragEnd = async (event: any, info: any) => {
    if (isTransitioning) return;

    const offsetX = info.offset?.x || 0;
    const offsetY = info.offset?.y || 0;
    const velocityX = info.velocity?.x || 0;
    const velocityY = info.velocity?.y || 0;
    
    const swipeThreshold = 100;
    const velocityThreshold = 400;

    let determinedDirection: 'left' | 'right' | 'up' | null = null;

    // Determine swipe direction based on offset and velocity
    if (Math.abs(offsetY) > swipeThreshold && Math.abs(velocityY) > velocityThreshold && offsetY < 0) {
      determinedDirection = 'up';
    } else if (Math.abs(offsetX) > swipeThreshold || Math.abs(velocityX) > velocityThreshold) {
      determinedDirection = offsetX < 0 ? 'left' : 'right';
    }

    // If no significant swipe, animate back to center
    if (!determinedDirection) {
      animateCard.start({ x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 });
      return;
    }

    // Set the direction state for the exit animation
    setDirection(determinedDirection);

    // Start exit animation based on direction
    let exitAnimation = {};
    if (determinedDirection === 'up') {
      exitAnimation = { y: -500, opacity: 0, transition: { duration: 0.3 } };
    } else {
      exitAnimation = { 
        x: determinedDirection === 'left' ? -500 : 500, 
        opacity: 0, 
        rotate: determinedDirection === 'left' ? -20 : 20,
        transition: { duration: 0.3 } 
      };
    }
    
    // Wait for the exit animation to complete
    await animateCard.start(exitAnimation);

    // Perform action based on swipe direction (after animation)
    if (determinedDirection === 'right') {
      // Show message overlay instead of directly selecting vendor
      setSelectedVendor(vendors[currentIndex]);
      setShowMessageOverlay(true);
    } else if (determinedDirection === 'up') {
      try {
        const vendorId = vendors[currentIndex]?.id;
        if (vendorId) {
          await FavoritesAPI.addVendorToFavorites(String(vendorId));
        }
      } catch (error) {
        console.error('Failed to favorite vendor:', error);
      }
      // Advance to next card
      advanceCard();
    } else {
      // Left swipe just advances to next card
      advanceCard();
    }
  };

  // Calculate card styles
  const getCardStyles = (index: number) => ({
    scale: 1 - index * 0.02,
    y: index * 5,
    rotate: 0,
    zIndex: 3 - index,
    opacity: 1 - index * 0.12,
  });

  // Handle message button click
  const handleMessageButtonClick = () => {
    setShowMessageOverlay(false);
    setShowQuoteModal(true);
  };

  if (isTransitioning) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-white/30 backdrop-blur-md rounded-xl border border-white/40">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#FF4E8E] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-[#5D5FEF]/70">Loading vendors...</p>
        </div>
      </div>
    );
  }

  if (!vendors || vendors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full bg-white/30 backdrop-blur-md rounded-xl border border-white/40 p-4">
        <div className="w-16 h-16 mb-4 text-[#5D5FEF] opacity-50">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        </div>
        <p className="text-sm text-[#5D5FEF] text-center">No vendors found in this category</p>
        <p className="text-xs text-[#5D5FEF]/70 text-center mt-2">Try selecting a different category</p>
      </div>
    );
  }

  return (
    <>
      <div className="relative h-full w-full overflow-hidden pb-safe px-4 py-4">
        <div className="h-full w-full relative">
          <div className="h-full w-full relative pt-0">
            <AnimatePresence initial={false} mode="wait">
              {currentVendors.map((vendor, index) => {
                const styles = getCardStyles(index);
                
                return (
                  <motion.div
                    key={`${vendor.id}-${currentIndex}`}
                    style={{
                      x: index === 0 ? x : 0,
                      y: index === 0 ? y : styles.y,
                      rotate: index === 0 ? rotate : styles.rotate,
                      scale: index === 0 ? scaleOnDrag : styles.scale,
                      zIndex: styles.zIndex,
                      position: 'absolute',
                      inset: 0,
                    }}
                    initial={{ 
                      scale: index === 0 ? 0.95 : styles.scale,
                      y: index === 0 ? 20 : styles.y,
                      opacity: 0 
                    }}
                    animate={index === 0 ? animateCard : {
                      scale: styles.scale,
                      y: styles.y,
                      opacity: styles.opacity,
                      rotate: styles.rotate,
                      transition: { type: "spring", stiffness: 300, damping: 20 }
                    }}
                    exit={{ 
                      scale: 0.95,
                      y: -20,
                      opacity: 0,
                      transition: { duration: 0.2 }
                    }}
                    drag={index === 0 && !isTransitioning}
                    dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
                    onDragEnd={handleDragEnd}
                    className="w-full h-full rounded-2xl overflow-hidden shadow-lg border border-white/30 backdrop-blur-sm"
                  >
                    {/* Card Content - Combined with parent div */}
                    <img
                      src={vendor.image || '/themesOption/placeholder.jpg'}
                      alt={vendor.name}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    
                    {/* Top Badges Row - Repositioned as circular badges with reduced size */}
                    <div className="absolute top-4 left-4 flex gap-2 z-20">
                      {vendor.verified && (
                        <div className="w-10 h-10 rounded-full bg-[#FF4E8E]/90 backdrop-blur-md flex items-center justify-center border border-[#FF4E8E]/30 shadow-lg">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                      )}
                      {vendor.topVendor && (
                        <div className="w-10 h-10 rounded-full bg-[#8A2BE2]/90 backdrop-blur-md flex items-center justify-center border border-[#8A2BE2]/30 shadow-lg">
                          <Award className="w-5 h-5 text-white" />
                        </div>
                      )}
                      {vendor.approximateCharge && (
                        <div className="w-10 h-10 rounded-full bg-[#FFB02E]/90 backdrop-blur-md flex items-center justify-center border border-[#FFB02E]/30 shadow-lg">
                          <span className="text-xs font-semibold text-white">${Math.round(vendor.approximateCharge/1000)}K</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Bottom dark overlay */}
                    <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/90 via-black/70 to-transparent" />
                    
                    {/* Vendor Info - At bottom but above action buttons */}
                    <div className="absolute inset-x-0 bottom-20 p-2 z-10">
                      {/* Name with verified badge inline */}
                      <div className="flex items-center mb-1">
                        <h2 className="text-2xl font-semibold text-white">
                          {vendor.name}
                        </h2>
                        {vendor.verified && (
                          <BadgeCheck className="w-5 h-5 text-[#2DCEA8] ml-1.5" />
                        )}
                      </div>
                      
                      {/* Location with pin icon */}
                      <div className="flex items-center gap-1 mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-white/90">
                          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <p className="text-white/90 text-sm">{vendor.location}</p>
                      </div>
                      
                      {/* Star Rating as actual stars */}
                      {vendor.rating && (
                        <div className="flex items-center">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star 
                                key={star} 
                                className={`w-4 h-4 ${Number(vendor.rating) >= star ? 'text-[#FFB02E] fill-[#FFB02E]' : 'text-[#FFB02E]/40 fill-[#FFB02E]/40'}`} 
                              />
                            ))}
                          </div>
                          <span className="text-white font-medium text-sm ml-2">{Number(vendor.rating).toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Control buttons - Moved to absolute bottom */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 z-30 rounded-full">
                      <button
                        onClick={() => setCurrentIndex(Math.floor(Math.random() * vendors.length))}
                        className="w-12 h-12 flex items-center justify-center rounded-full bg-[#8A2BE2] text-white shadow-md active:scale-95 transition-all border border-white/30"
                        style={{ transform: 'translateY(-10px)' }}
                      >
                        <Shuffle className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDragEnd({}, { offset: { x: -200, y: 0 }, velocity: { x: -500, y: 0 } })}
                        className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FF6B6B] text-white shadow-lg active:scale-95 transition-all border border-white/30"
                        style={{ transform: 'translateY(-5px)' }}
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDragEnd({}, { offset: { x: 0, y: -200 }, velocity: { x: 0, y: -500 } })}
                        className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FFB02E] text-white shadow-lg active:scale-95 transition-all border border-white/30"
                        style={{ transform: 'translateY(0px)' }}
                      >
                        <Heart className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDragEnd({}, { offset: { x: 200, y: 0 }, velocity: { x: 500, y: 0 } })}
                        className="w-12 h-12 flex items-center justify-center rounded-full bg-[#5D5FEF] shadow-lg active:scale-95 transition-all border border-white/30"
                        style={{ transform: 'translateY(-5px)' }}
                      >
                        <MessageCircle className="w-5 h-5 text-white" />
                      </button>
                      <button
                        onClick={() => setCurrentIndex((prev) => (prev - 1 + vendors.length) % vendors.length)}
                        className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FF4E8E] text-white shadow-md active:scale-95 transition-all border border-white/30"
                        style={{ transform: 'translateY(-10px)' }}
                      >
                        <RotateCcw className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Swipe Indicators */}
                    {index === 0 && (
                      <>
                        <motion.div
                          className="absolute left-6 top-1/2 -translate-y-1/2 bg-gradient-to-r from-[#FF6B6B] to-[#FF4E8E] backdrop-blur-md rounded-full p-3 shadow-md border border-white/20 pointer-events-none"
                          style={{
                            opacity: leftIndicatorOpacity,
                          }}
                        >
                          <X className="w-6 h-6 text-white" />
                        </motion.div>
                        <motion.div
                          className="absolute right-6 top-1/2 -translate-y-1/2 bg-gradient-to-r from-[#2DCEA8] to-[#5D5FEF] backdrop-blur-md rounded-full p-3 shadow-md border border-white/20 pointer-events-none"
                          style={{
                            opacity: rightIndicatorOpacity,
                          }}
                        >
                          <MessageCircle className="w-6 h-6 text-white" />
                        </motion.div>
                        <motion.div
                          className="absolute top-6 left-1/2 -translate-x-1/2 bg-gradient-to-b from-[#FFD700] to-[#FFA500] backdrop-blur-md rounded-full p-3 shadow-md border border-white/20 pointer-events-none"
                          style={{
                            opacity: upIndicatorOpacity,
                          }}
                        >
                          <Heart className="w-6 h-6 text-white" />
                        </motion.div>
                        
                        {/* Center indicator that follows the swipe direction */}
                       
                      </>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Message Overlay */}
      <AnimatePresence>
        {showMessageOverlay && selectedVendor && (
          <MessageOverlay
            vendor={selectedVendor}
            onClose={() => {
              setShowMessageOverlay(false);
              advanceCard();
            }}
            onSendMessage={() => {
              handleMessageButtonClick();
            }}
            onViewDetails={() => {
              setShowMessageOverlay(false);
              onViewDetails(selectedVendor);
            }}
          />
        )}
      </AnimatePresence>

      {/* Quote Request Modal */}
      <AnimatePresence>
        {showQuoteModal && selectedVendor && (
          <QuoteRequestModal
            isOpen={showQuoteModal}
            onClose={() => {
              setShowQuoteModal(false);
              advanceCard();
            }}
            vendor={selectedVendor}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VendorSwipeView; 